import React, { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Satellite } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { hasPerm } from "../../lib/permissions";
import { PageHead, Field, Empty, ago } from "./common";

const BLANK = { vessel_name: "", imo: "", mmsi: "", vessel_type: "Container Ship", origin_port: "", destination_port: "", eta: "", current_status: "", latitude: "", longitude: "", ais_timestamp: "" };

export default function VesselTracking() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const canCreate = hasPerm(user, "sea_freight", "create");
  const canDelete = hasPerm(user, "sea_freight", "delete");

  const load = useCallback(() => {
    api.get("/sea-freight/vessels").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      await api.post("/sea-freight/vessels", form);
      toast.success("Vessel added to the watchlist");
      setOpen(false); setForm(BLANK); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/sea-freight/vessels/${id}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="vessel-tracking-page">
      <PageHead title="Vessel Tracking"
        subtitle="Vessel watchlist with AIS position and ETA. Positions are only shown from a licensed AIS provider or your own entry — nothing is estimated."
        right={canCreate && <Button data-testid="add-vessel-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm gap-1.5" onClick={() => setOpen(true)}><Plus className="w-4 h-4" /> Add vessel</Button>} />

      {data && !data.ais_connected && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-sm px-4 py-3 text-sm mb-4 flex items-start gap-2" data-testid="ais-disabled-note">
          <Satellite className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <b>Live AIS is not connected.</b> {data.message}
            <div className="text-xs mt-0.5">Supported providers: {data.providers.join(", ")}.</div>
          </div>
        </div>
      )}

      {data && data.vessels.length === 0 ? <Empty testid="vessels-empty">No vessels on the watchlist yet.</Empty> : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto" data-testid="vessels-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>{["Vessel", "IMO", "MMSI", "Type", "Position", "Origin", "Destination", "ETA", "Status", "AIS timestamp", "Data source", ""].map((h) => (
                <th key={h} className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">{h}</th>))}</tr>
            </thead>
            <tbody>
              {(data?.vessels || []).map((v) => (
                <tr key={v.id} data-testid={`vessel-row-${v.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-2.5 text-slate-800 whitespace-nowrap">{v.vessel_name}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{v.imo || "-"}</td>
                  <td className="px-3 py-2.5 font-mono text-xs">{v.mmsi || "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{v.vessel_type}</td>
                  <td className="px-3 py-2.5 text-xs text-slate-500 whitespace-nowrap">{v.latitude != null ? `${v.latitude}, ${v.longitude}` : "no position"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{v.origin_port || "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{v.destination_port || "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{v.eta || "-"}</td>
                  <td className="px-3 py-2.5 text-slate-600">{v.current_status}</td>
                  <td className="px-3 py-2.5 whitespace-nowrap text-[11px] text-slate-500">
                    {v.ais_timestamp ? ago(v.ais_timestamp) : "manual entry"}
                    {v.is_stale && <Badge variant="outline" className="ml-1 rounded-full text-[10px] px-1.5 bg-slate-100 text-slate-600 border-slate-200">stale</Badge>}
                  </td>
                  <td className="px-3 py-2.5 text-[11px] text-slate-500 max-w-[200px] truncate">{v.data_source}</td>
                  <td className="px-3 py-2.5 text-right">
                    {canDelete && <Button data-testid={`delete-vessel-${v.id}`} size="icon" variant="ghost" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => remove(v.id)}><Trash2 className="w-4 h-4" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="add-vessel-dialog">
          <DialogHeader><DialogTitle className="font-head">Add vessel to watchlist</DialogTitle>
            <DialogDescription>Positions and ETA stay as entered until a licensed AIS provider is connected.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Field label="Vessel name"><Input data-testid="vessel-name" value={form.vessel_name} onChange={(e) => setForm({ ...form, vessel_name: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="IMO number"><Input data-testid="vessel-imo" value={form.imo} onChange={(e) => setForm({ ...form, imo: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="MMSI"><Input data-testid="vessel-mmsi" value={form.mmsi} onChange={(e) => setForm({ ...form, mmsi: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Vessel type"><Input data-testid="vessel-type" value={form.vessel_type} onChange={(e) => setForm({ ...form, vessel_type: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Origin port"><Input data-testid="vessel-origin" value={form.origin_port} onChange={(e) => setForm({ ...form, origin_port: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Destination port"><Input data-testid="vessel-destination" value={form.destination_port} onChange={(e) => setForm({ ...form, destination_port: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Estimated arrival"><Input data-testid="vessel-eta" type="date" value={form.eta} onChange={(e) => setForm({ ...form, eta: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Latitude"><Input data-testid="vessel-lat" type="number" value={form.latitude} onChange={(e) => setForm({ ...form, latitude: e.target.value })} className="bg-white h-9" /></Field>
            <Field label="Longitude"><Input data-testid="vessel-lon" type="number" value={form.longitude} onChange={(e) => setForm({ ...form, longitude: e.target.value })} className="bg-white h-9" /></Field>
            <div className="sm:col-span-3"><Field label="Current status"><Input data-testid="vessel-status" value={form.current_status} onChange={(e) => setForm({ ...form, current_status: e.target.value })} placeholder="e.g. En route, At anchor, Berthed" className="bg-white h-9" /></Field></div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="save-vessel-btn" className="bg-[#0B132B] hover:bg-[#1C2541] text-white rounded-sm" onClick={save}>Save vessel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
